"use client";

import type { ReactNode } from "react";

/**
 * Le gabarit de la bibliothèque : sidebar à gauche, canevas à droite.
 *
 * Copié de `components/studio/studio-frame.tsx` (les deux branches sidebar et
 * le voile), amputé de tout ce qui n'a de sens qu'avec un piano-roll derrière :
 * `usePianoHeight` et `useMinimapWidth` réserveraient ici une bande morte pour
 * un clavier absent, et les emplacements `overlay` / `fullCanvasOverlay` /
 * `transient` / `rightPanel` n'ont aucun consommateur dans cette vue.
 *
 * # Divergence assumée avec le studio : le seuil de tiroir
 *
 * Le studio replie sa sidebar en tiroir dès qu'on passe sous `expanded`
 * (1024 px) parce que la largeur du canvas y pilote la taille des touches :
 * 200 px pris au roll, ce sont des touches plus étroites, donc un instrument
 * moins jouable. La bibliothèque n'a pas cette contrainte — une grille de
 * cartes se reflue, un clavier non — et masquer la carte du répertoire sur une
 * tablette retirerait précisément ce qui rend l'écran parcourable.
 *
 * D'où **768 px** ici et non 1024 : la sidebar reste en flux dès la tablette
 * en portrait, et ne passe en tiroir + voile qu'en dessous. C'est écrit noir
 * sur blanc parce que l'incohérence avec le studio saute aux yeux et que
 * quelqu'un la « corrigera » dans six mois s'il ne trouve pas la raison.
 *
 * # Largeur
 *
 * `min(240px, 40vw)` contre `min(200px, 40vw)` au studio : la sidebar porte
 * ici des noms de sélections (« Des mélodies que tu connais ») et non des degrés
 * romains. La borne à 40 vw ne mord qu'en dessous de 600 px de large, là où le
 * tiroir couvre le canevas et où le reste de l'écran est la seule zone qui
 * permet de le refermer au doigt.
 */
const SIDEBAR_WIDTH = "min(240px, 40vw)";

interface BrowseFrameProps {
  sidebar: ReactNode;
  children: ReactNode;
  /** Le tiroir est ouvert. N'a de sens que sous 768 px. */
  drawerOpen: boolean;
  /** Demande de fermeture émise par le voile du tiroir. */
  onCloseSidebar: () => void;
}

export const BrowseFrame = ({ sidebar, children, drawerOpen, onCloseSidebar }: BrowseFrameProps) => {
  return (
    <div className="flex-1 flex min-h-0 relative">
      {/*
        Voile translucide et non `bg-overlay-deep` : on ne masque pas le
        catalogue, on le met en retrait. Un voile opaque transformerait
        l'ouverture d'un panneau latéral en changement de page.
      */}
      {drawerOpen && (
        <div
          className="absolute inset-0 z-30 bg-glass-backdrop md:hidden"
          aria-hidden="true"
          onClick={onCloseSidebar}
        />
      )}

      {/*
        Tiroir en dessous de `md`, panneau en flux au-dessus — décidé par le
        CSS et non par un `useBreakpoint()`.

        Le hook ne peut pas savoir : `useMediaQuery` renvoie `false` au premier
        rendu, donc il annonce `compact` sur le serveur et à la première frame
        client. Tant que rien n'était rendu côté serveur, ce battement d'une
        frame ne se voyait pas ; depuis que la bibliothèque sort pleine du
        serveur, le navigateur *peint* la version repliée avant de se corriger
        — un poste desktop voyait donc la page s'ouvrir sidebar fermée, puis
        sauter. Le CSS, lui, est juste dès le premier octet.

        `hidden` (donc `display: none`) sort déjà le panneau de l'ordre de
        tabulation et de l'arbre d'accessibilité quand le tiroir est fermé :
        `inert` et `aria-hidden` n'ont plus rien à ajouter.
      */}
      <div
        id="library-sidebar"
        className={`absolute inset-y-0 left-0 z-30 h-full flex-col bg-bg-surface border-r border-border-subtle
          md:static md:z-auto md:flex md:shrink-0 ${drawerOpen ? "flex" : "hidden"}`}
        style={{ width: SIDEBAR_WIDTH }}
      >
        {sidebar}
      </div>

      <div className="flex-1 min-w-0 min-h-0 relative flex flex-col">{children}</div>
    </div>
  );
};
